import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import SearchIcon from "@mui/icons-material/Search";
import React, {useEffect, useState} from "react";
import {Box, CircularProgress, Menu, MenuItem, Paper, Stack, Tooltip} from "@mui/material";
import {DataGrid} from "@mui/x-data-grid";
import {useNavigate} from "react-router-dom";
import {api} from "../../services/apiService";

import {formatDateOnlyISO} from "../../utils/helpers";

import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import WarningIcon from '@mui/icons-material/Warning';
import {API_BASE_URL} from "../../config";

export default function Invoices() {
    const navigate = useNavigate();
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [datagridLoading, setDatagridLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [totalInvoices, setTotalInvoices] = useState(0);
    const [statusSummary, setStatusSummary] = useState([]);
    const [query, setQuery] = useState("");

    // menu state
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedRow, setSelectedRow] = useState(null);

    const columns = [
        {
            field: "status",
            headerName: "",
            width: 5,
            renderCell: (params) => {
                const icon = params.value.icon;
                if (icon === "paid") {
                    return <Tooltip title={params.value.label}><CheckCircleIcon sx={{color: 'green', height: '100%'}}/></Tooltip>;
                } else if (icon === "waiting") {
                    return <Tooltip title={params.value.label}><HourglassTopIcon
                        style={{color: '#ffc864', height: '100%'}}/></Tooltip>;
                } else if (icon === "cancelled") {
                    return <Tooltip title={params.value.label}><CancelIcon
                        style={{color: 'red', height: '100%'}}/></Tooltip>;
                } else if (icon === "overdue") {
                    return <Tooltip title={params.value.label}><WarningIcon
                        style={{color: 'darkorange', height: '100%'}}/></Tooltip>;
                } else {
                    return null;
                }
            }
        },
        {field: "ref", headerName: "N° Facture", flex: 1, minWidth: 120},
        {field: "client", headerName: "Client", flex: 1.2, minWidth: 180, valueGetter: params => params.name},
        {
            field: "created_at",
            headerName: "Date de création",
            flex: 1,
            minWidth: 100,
            valueGetter: params => formatDateOnlyISO(params)
        },
        {
            field: "due_date",
            headerName: "Date d'échéance",
            flex: 1,
            minWidth: 100,
            valueGetter: params => formatDateOnlyISO(params)
        },
        {
            field: "total_ttc",
            headerName: "Montant TTC (€)",
            flex: 1,
            minWidth: 120,
            valueFormatter: (params) =>
                params.toLocaleString("fr-FR", {
                    style: "currency",
                    currency: "EUR",
                }),
        },
        {
            field: "type",
            headerName: "Type",
            minWidth: 80,
            valueFormatter: (params) => {
                switch (params) {
                    case "credit":
                        return "Avoir";
                    case "deposit":
                        return "Acompte";
                    case "supplement":
                        return "Supplément";
                    case "final":
                        return "Solde";
                    default:
                        return "";
                }
            },
        }
    ];

    useEffect(() => {
        api.get("/invoices/get_status_list")
            .then((response) => {
                setStatusSummary(response.data);
                setLoading(false);
            })
    }, []);


    useEffect(() => {
        const fetchData = async () => {
            setDatagridLoading(true);

            api.get("/invoices/list?page=" + (page + 1) + "&per_page=" + perPage + (query ? "&query=" + encodeURIComponent(query) : ""))
                .then((response) => {
                    setInvoices(response.data.invoices);
                    setTotalInvoices(response.data.total);
                    setDatagridLoading(false);
                });
        }

        const timeout = setTimeout(() => {
            void fetchData();
        }, 200);

        return () => clearTimeout(timeout);
    }, [page, perPage, query]);

    const handleRowClick = (event, row) => {
        setSelectedRow(row);
        setAnchorEl(event.currentTarget || event.target);
    };

    const handleClose = () => {
        setAnchorEl(null);
        setSelectedRow(null);
    };

    const openInvoice = () => {
        if (!selectedRow) return handleClose();

        window.open(`${API_BASE_URL}/invoices/view/${selectedRow.id}`, '_blank');
        handleClose();
    };

    const openBooking = () => {
        if (!selectedRow) return handleClose();

        const bookingId = selectedRow.booking_id;
        navigate(`/bookings/details/${bookingId}`);
        handleClose();
    };

    return (
        !loading ?
            <Container sx={{overflowX: 'auto', width: '100%'}}>
                <Typography variant="h1" sx={{mb: 3}}>Factures</Typography>

                <Grid container spacing={2}>
                    {statusSummary.map((item, index) => (
                        <Grid item sm={12} md={4} key={index} sx={{display: 'flex', flexGrow: 1, minWidth: "70px"}}>
                            <Card sx={{flex: 1}}>
                                <CardContent>
                                    <Typography
                                        component="h2"
                                        sx={{fontSize: '1rem', fontWeight: 'bold', mb: 1}}
                                    >
                                        {item.label}
                                    </Typography>
                                    <Typography
                                        component="p"
                                        sx={{fontSize: '1.5rem'}}
                                    >
                                        {item.count}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>

                <Grid container spacing={2} sx={{display: 'flex', justifyContent: 'space-between', mt: '2rem'}}>
                    <TextField
                        sx={{width: {xs: '100%', md: 'auto'}}}
                        placeholder="Rechercher une facture..."
                        variant="outlined"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon/>
                                </InputAdornment>
                            ),
                        }}
                        onChange={e => setQuery(e.target.value)}
                        value={query}
                    />

                </Grid>

                <Paper sx={{height: 400, width: '100%', mt: '2rem'}}>
                    <DataGrid
                        rows={invoices}
                        columns={columns}
                        paginationModel={{page: page, pageSize: perPage}}
                        onPaginationModelChange={(model) => {
                            setPage(model.page);
                            setPerPage(model.pageSize);
                        }}
                        loading={datagridLoading}
                        pageSizeOptions={[10, 25, 50]}
                        rowCount={totalInvoices}
                        paginationMode="server"
                        onRowClick={(params, event) => handleRowClick(event, params.row)}
                        sx={{
                            border: 0,
                            cursor: "pointer",
                            "& .MuiDataGrid-row:hover": {
                                backgroundColor: "#f0f0f0",
                            },
                            backgroundColor: "#fff",
                        }}
                    />
                </Paper>

                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleClose}
                    anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
                    transformOrigin={{vertical: 'top', horizontal: 'left'}}
                >
                    <MenuItem onClick={openInvoice}>Ouvrir la facture</MenuItem>
                    <MenuItem onClick={openBooking}>Ouvrir la réservation</MenuItem>
                </Menu>
            </Container>
            :
            <Container maxWidth="lg" sx={{
                mt: 4,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "60vh"
            }}>
                <Stack spacing={2} alignItems="center">
                    <Box>
                        <Typography variant="h6" sx={{mb: 2}}>Chargement...</Typography>
                        <Box sx={{display: "flex", justifyContent: "center"}}>
                            <CircularProgress color="primary"/>
                        </Box>
                    </Box>
                </Stack>
            </Container>
    );
}
